import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const APP_LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/8c23fucn_Logo%20Censo911.png";

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
};

const folio = (plantelCct, personaId) => {
  const short = (personaId || "").replace(/-/g, "").slice(0, 6).toUpperCase();
  return `${plantelCct || "SIN"}-${short}`;
};

const fmt = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const upper = (s) => (s || "").toUpperCase();

const buildQrUrl = ({ tipo, item, plantel, ciclo }) => {
  // Siempre apuntamos al dominio de producción para que el escaneo abra la página
  // pública desde cualquier lector (evita URLs largas de preview y garantiza que
  // el escáner lo detecte como enlace web).
  const base = "https://censo911.xyz";
  const p = new URLSearchParams();
  p.set("tipo", tipo === "ALUMNO" ? "alumno" : "docente");
  p.set("n", item.nombre || "");
  if (tipo === "ALUMNO") {
    if (item.grado != null) p.set("g", String(item.grado));
    if (item.letra) p.set("gr", item.letra);
  } else {
    if (item.puesto) p.set("p", item.puesto);
  }
  if (ciclo) p.set("c", ciclo);
  if (plantel?.cct) p.set("cct", plantel.cct);
  if (plantel?.nombre) p.set("pl", plantel.nombre);
  p.set("f", folio(plantel?.cct, item.id));
  // Reemplazamos "+" (espacios) por "%20", que muchos escáneres nativos reconocen
  // mejor como parte de una URL y disparan la sugerencia "Abrir en el navegador".
  const qs = p.toString().replace(/\+/g, "%20");
  return `${base}/verificar?${qs}`;
};

export default function CredencialesPrint() {
  const [sp] = useSearchParams();
  const plantelId = sp.get("plantel_id");
  const tipo = sp.get("tipo") || "ALUMNO";
  const alcance = sp.get("alcance") || "TODOS";
  const grupoId = sp.get("grupo_id");
  const personaId = sp.get("persona_id");
  const vigencia = sp.get("vigencia");
  const ciclo = sp.get("ciclo");

  const [plantel, setPlantel] = useState(null);
  const [items, setItems] = useState([]); // [{id, nombre, curp, rfc, grado, letra, puesto}]
  const [directorName, setDirectorName] = useState("");
  const [modo, setModo] = useState("COLOR"); // COLOR | BN
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const plantelesResp = await api.get("/planteles");
        const p = plantelesResp.data.find((x) => x.id === plantelId);
        setPlantel(p || null);
        // Nombre del director para el reverso (siempre)
        try {
          const staffResp = await api.get(`/planteles/${plantelId}/staff-list`);
          const dir = staffResp.data?.director;
          if (dir) setDirectorName([dir.nombre, dir.apellido_paterno, dir.apellido_materno].filter(Boolean).join(" "));
        } catch { /* ignore */ }
        let arr = [];
        if (tipo === "DOCENTE") {
          const { data } = await api.get(`/planteles/${plantelId}/staff-list`);
          let docentes = data.docentes || [];
          if (alcance === "INDIVIDUAL" && personaId) docentes = docentes.filter((d) => d.id === personaId);
          const personalFn = data.personal_funcion || [];
          const puestoDoc = "DOCENTE";
          arr = docentes.map((d) => ({
            id: d.id,
            nombre: [d.nombre, d.apellido_paterno, d.apellido_materno].filter(Boolean).join(" "),
            curp: d.curp || "",
            rfc: d.rfc || "",
            puesto: puestoDoc,
            numero_empleado: d.plaza || d.id.slice(0, 8).toUpperCase(),
            fecha_ingreso: d.fecha_ingreso_sep || "",
          }));
          // Include director si el usuario actual es director del plantel
          if (p?.director_user_id && alcance === "TODOS") {
            const dir = data.director;
            if (dir) arr.unshift({
              id: dir.id,
              nombre: [dir.nombre, dir.apellido_paterno, dir.apellido_materno].filter(Boolean).join(" "),
              curp: dir.curp || "",
              rfc: dir.rfc || "",
              puesto: "DIRECTOR(A)",
              numero_empleado: dir.plaza || dir.id.slice(0, 8).toUpperCase(),
              fecha_ingreso: dir.fecha_ingreso_sep || "",
            });
          }
          // Personal por función
          if (alcance === "TODOS") {
            personalFn.forEach((pf) => arr.push({
              id: pf.id,
              nombre: [pf.nombre, pf.apellido_paterno, pf.apellido_materno].filter(Boolean).join(" "),
              curp: pf.curp || "",
              rfc: pf.rfc || "",
              puesto: (pf.funcion || "").replace(/_/g, " "),
              numero_empleado: pf.plaza || pf.id.slice(0, 8).toUpperCase(),
              fecha_ingreso: pf.fecha_ingreso_sep || "",
            }));
          }
        } else {
          let alumnos = [];
          if (alcance === "TODOS") {
            const gs = await api.get("/grupos", { params: { plantel_id: plantelId } });
            for (const g of gs.data) {
              const { data } = await api.get("/alumnos", { params: { grupo_id: g.id } });
              data.forEach((a) => alumnos.push({ ...a, __grado: g.grado, __letra: g.letra, __ciclo: g.ciclo }));
            }
          } else if (alcance === "GRUPO" && grupoId) {
            const g = (await api.get(`/grupos/${grupoId}`)).data;
            const { data } = await api.get("/alumnos", { params: { grupo_id: grupoId } });
            alumnos = data.map((a) => ({ ...a, __grado: g.grado, __letra: g.letra, __ciclo: g.ciclo }));
          } else if (alcance === "INDIVIDUAL" && personaId) {
            // buscamos en todos los grupos del plantel
            const gs = await api.get("/grupos", { params: { plantel_id: plantelId } });
            for (const g of gs.data) {
              const { data } = await api.get("/alumnos", { params: { grupo_id: g.id } });
              const found = data.find((a) => a.id === personaId);
              if (found) { alumnos = [{ ...found, __grado: g.grado, __letra: g.letra, __ciclo: g.ciclo }]; break; }
            }
          }
          arr = alumnos.map((a) => ({
            id: a.id,
            nombre: [a.nombre, a.apellido_paterno, a.apellido_materno].filter(Boolean).join(" "),
            curp: a.curp || "",
            grado: a.__grado,
            letra: a.__letra,
            ciclo_grupo: a.__ciclo,
          }));
        }
        setItems(arr);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [plantelId, tipo, alcance, grupoId, personaId]);

  const individual = alcance === "INDIVIDUAL" && items.length === 1;

  // Split into pages: 3 credenciales por hoja (frente + reverso al costado)
  const paginas = useMemo(() => {
    if (individual) return [items];
    const out = [];
    for (let i = 0; i < items.length; i += 3) out.push(items.slice(i, i + 3));
    return out;
  }, [items, individual]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Cargando credenciales…</div>;
  if (!items.length) return <div className="min-h-screen flex items-center justify-center text-[#9F2241]">Sin datos para credenciales</div>;

  const cicloShow = ciclo || items[0]?.ciclo_grupo || "";

  return (
    <div className={`min-h-screen bg-[#EAECEE] print:bg-white p-6 print:p-0 ${modo === "BN" ? "cred-bn" : ""}`}>
      <div className="mb-4 flex justify-between items-center print:hidden">
          <div className="text-sm text-[#374151]">
            {items.length} credencial{items.length === 1 ? "" : "es"} · {tipo === "ALUMNO" ? "Alumnos" : "Docentes"} · {plantel?.nombre}
            {!individual && <span className="ml-2 text-[#6B7280]">· 3 por hoja (frente + reverso)</span>}
          </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded border border-[#D1D5DB] overflow-hidden text-xs" data-testid="cred-modo">
            <button
              onClick={() => setModo("COLOR")}
              className={`px-3 py-1.5 ${modo === "COLOR" ? "bg-[#235B4E] text-white" : "bg-white text-[#374151]"}`}
              data-testid="cred-modo-color"
            >Color</button>
            <button
              onClick={() => setModo("BN")}
              className={`px-3 py-1.5 border-l border-[#D1D5DB] ${modo === "BN" ? "bg-[#111827] text-white" : "bg-white text-[#374151]"}`}
              data-testid="cred-modo-bn"
            >Blanco y negro</button>
          </div>
          <button onClick={() => window.print()} className="px-3 py-1.5 rounded bg-[#235B4E] text-white text-sm">Imprimir</button>
        </div>
      </div>

      {individual ? (
        <IndividualLayout item={items[0]} plantel={plantel} tipo={tipo} vigencia={vigencia} ciclo={cicloShow} directorName={directorName} />
      ) : (
        paginas.map((page, idx) => (
          <div key={idx} className="cred-page bg-white mx-auto mb-6 print:mb-0">
            <div className="flex flex-col gap-[6mm] p-[6mm]">
              {page.map((item) => (
                <div key={item.id} className="flex gap-[4mm] items-start">
                  <CardFrente item={item} plantel={plantel} tipo={tipo} vigencia={vigencia} ciclo={cicloShow} />
                  <CardReverso item={item} plantel={plantel} tipo={tipo} directorName={directorName} />
                </div>
              ))}
              {Array(3 - page.length).fill(0).map((_, i) => (
                <div key={`e-${i}`} style={{ height: "60mm" }} />
              ))}
            </div>
          </div>
        ))
      )}

      <style>{`
        @page { size: Letter portrait; margin: 0; }
        .cred-page { width: 216mm; height: 279mm; page-break-after: always; }
        .cred-page:last-child { page-break-after: auto; }
        .cred-card { width: 96mm; height: 60mm; overflow: hidden; }
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; background: white; }
          .cred-page { box-shadow: none !important; margin: 0 !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        /* Modo Blanco y Negro: elimina fondos oscuros y colorea con negro sobre blanco.
           Prioriza legibilidad al imprimir en impresoras B/N. */
        .cred-bn .cred-card { border: 1.5px solid #000; }
        .cred-bn .cred-hdr,
        .cred-bn .cred-hdr-rev {
          background: #fff !important;
          color: #000 !important;
          border-bottom: 1.5px solid #000;
        }
        .cred-bn .cred-hdr .opacity-80,
        .cred-bn .cred-hdr .opacity-70,
        .cred-bn .cred-hdr-rev .opacity-70 { opacity: 1 !important; }
        .cred-bn .cred-hdr img,
        .cred-bn .cred-hdr-rev img { background: #fff !important; filter: grayscale(100%) contrast(1.2); }
        .cred-bn .cred-photo {
          background: #fff !important;
          color: #000 !important;
          border: 1.5px dashed #000;
        }
        .cred-bn .cred-photo .cred-initials { color: #000 !important; }
        .cred-bn .text-[#6B7280],
        .cred-bn .text-[#9CA3AF] { color: #4B5563 !important; }
        .cred-bn .bg-[#F0FDF4],
        .cred-bn .bg-[#F9FAFB] { background: #fff !important; }
      `}</style>
    </div>
  );
}

function CardFrente({ item, plantel, tipo, vigencia, ciclo }) {
  const isAlumno = tipo === "ALUMNO";
  return (
    <div className="cred-card border border-black rounded overflow-hidden" style={{ fontFamily: "Arial, sans-serif" }}>
      <div className="cred-hdr bg-[#10312B] text-white flex items-center gap-2 px-2 py-1" style={{ height: "14mm" }}>
        {plantel?.logo_url ? (
          <img src={plantel.logo_url} alt="Logo" className="h-full object-contain bg-white rounded p-[0.3mm]" crossOrigin="anonymous" />
        ) : (
          <img src={APP_LOGO} alt="Censo 911" className="h-full object-contain bg-white rounded p-[0.3mm]" crossOrigin="anonymous" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[6.5pt] uppercase tracking-wider truncate opacity-80">{plantel?.nombre || ""}</div>
          <div className="text-[7pt] font-mono opacity-70">CCT: {plantel?.cct || ""} · Ciclo {ciclo || "—"}</div>
        </div>
        <div className="text-right">
          <div className="text-[6pt] opacity-70">CREDENCIAL</div>
          <div className="text-[8pt] font-bold uppercase">{isAlumno ? "Alumno" : "Docente"}</div>
        </div>
      </div>
      <div className="flex px-2 py-2 gap-2" style={{ height: "40mm" }}>
        <div className="cred-photo w-[25mm] h-[35mm] bg-[#9F2241] rounded flex items-center justify-center text-white shrink-0 self-start" title="Foto tamaño credencial (25 × 35 mm)">
          <div className="cred-initials text-[24pt] font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{initials(item.nombre)}</div>
        </div>
        <div className="min-w-0 flex-1 text-[8pt] leading-tight text-[#111827] pl-[3mm]">
          <div className="text-[6pt] uppercase tracking-wider text-[#6B7280]">Nombre</div>
          <div className="text-[9pt] font-bold uppercase leading-tight mb-1.5" style={{ wordBreak: "break-word" }}>{upper(item.nombre)}</div>
          <div className="text-[6pt] uppercase tracking-wider text-[#6B7280]">CURP</div>
          <div className="font-mono text-[7.5pt] mb-1">{item.curp || "—"}</div>
          {isAlumno ? (
            <>
              <div className="text-[6pt] uppercase tracking-wider text-[#6B7280]">Grado y Grupo</div>
              <div className="text-[8pt] font-semibold">{item.grado}° "{item.letra}"</div>
            </>
          ) : (
            <>
              <div className="text-[6pt] uppercase tracking-wider text-[#6B7280]">R.F.C. · Puesto</div>
              <div className="font-mono text-[7.5pt]">{item.rfc || "—"}</div>
              <div className="text-[7.5pt] font-semibold">{item.puesto || "—"}</div>
            </>
          )}
        </div>
        <div
          className="shrink-0 self-start flex flex-col items-center justify-start"
          style={{ width: "28mm" }}
          data-testid="cred-qr"
        >
          <QRCodeSVG
            value={buildQrUrl({ tipo, item, plantel, ciclo })}
            level="M"
            includeMargin={false}
            style={{ width: "28mm", height: "28mm", display: "block" }}
          />
          <div className="text-[5.5pt] uppercase tracking-widest text-[#6B7280] mt-[1mm]">Escanea</div>
        </div>
      </div>
      <div className="border-t border-black flex items-center px-2 justify-between text-[6.5pt]" style={{ height: "8mm" }}>
        <div><b>Folio:</b> {folio(plantel?.cct, item.id)}</div>
        {!isAlumno && <div><b>N° Empleado:</b> {item.numero_empleado}</div>}
        <div><b>Vigencia:</b> {fmt(vigencia)}</div>
      </div>
    </div>
  );
}

function CardReverso({ item, plantel, tipo, directorName }) {
  const isAlumno = tipo === "ALUMNO";
  const dirNombre = (directorName || "").trim();
  return (
    <div className="cred-card border border-black rounded overflow-hidden bg-white" style={{ fontFamily: "Arial, sans-serif" }}>
      <div className="cred-hdr-rev bg-[#10312B] text-white px-2 py-1.5 text-center" style={{ height: "10mm" }}>
        <div className="text-[8pt] font-bold uppercase tracking-wider">{plantel?.nombre}</div>
        <div className="text-[6pt] font-mono opacity-70">{plantel?.domicilio || ""}{plantel?.municipio ? ` · ${plantel.municipio}` : ""}{plantel?.entidad ? `, ${plantel.entidad}` : ""}</div>
      </div>
      <div className="p-2 text-[7.5pt] leading-snug flex flex-col justify-between" style={{ height: "50mm" }}>
        {isAlumno ? (
          <div>
            <div className="font-bold text-[8pt] mb-1">Aviso</div>
            <p className="text-[6.5pt] leading-tight">
              Esta credencial identifica al alumno como estudiante activo del plantel emisor durante el ciclo escolar vigente.
              En caso de extravío, favor de reportarlo a la escuela.
            </p>
          </div>
        ) : (
          <div>
            <div className="font-bold text-[8pt] mb-1">Portador</div>
            <div className="text-[7pt]"><b>Nombre:</b> {upper(item.nombre)}</div>
            {item.fecha_ingreso && <div className="text-[6.5pt]"><b>Fecha de ingreso:</b> {fmt(item.fecha_ingreso)}</div>}
          </div>
        )}
        <div className="pt-1 mt-1 text-center">
          <div className="mx-6 border-t border-black" />
          <div className="text-[7pt] font-bold uppercase mt-0.5">{upper(dirNombre) || "___________________________"}</div>
          <div className="text-[5.5pt] uppercase tracking-widest text-[#6B7280]">Director(a) del plantel</div>
        </div>
      </div>
    </div>
  );
}

function IndividualLayout({ item, plantel, tipo, vigencia, ciclo, directorName }) {
  return (
    <div className="cred-page bg-white mx-auto">
      <div className="p-[6mm] flex flex-col gap-[8mm] items-center">
        <div className="text-center">
          <div className="text-[10pt] uppercase tracking-widest text-[#6B7280]">Frente</div>
        </div>
        <CardFrente item={item} plantel={plantel} tipo={tipo} vigencia={vigencia} ciclo={ciclo} />
        <div className="text-center">
          <div className="text-[10pt] uppercase tracking-widest text-[#6B7280]">Reverso</div>
        </div>
        <CardReverso item={item} plantel={plantel} tipo={tipo} directorName={directorName} />
      </div>
    </div>
  );
}
